import CollaborationListItem from 'Components/campaigns/collaborations/CollaborationListItem';
import Loader from 'Components/Common/Loader';
import useDocumentTitle from 'hooks/useDocumentTitle';
import React, { useEffect, useMemo, useRef, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { updateCollaborationFilters, updateMilestoneCollaborationFilters } from 'slices/campaigns/collaborations/reducer';
import { fetchAllCollaborationsThunk, fetchCollaborations } from 'slices/campaigns/collaborations/thunk';
import { AppDispatch, RootState } from 'store';
import { Collaboration, CollaborationItem, MilestoneCollaborationsFilters, MilestoneCollaborationSortOptionLabel, MilestoneCollaborationSortOptions, SortOptions } from 'types/campaign/collaborations';
import { decodeBase64, encodeBase64, generateUniqueKey } from 'utils/helpers';
import 'react-perfect-scrollbar/dist/css/styles.css';
import PerfectScrollbar from 'react-perfect-scrollbar'
import { Container, DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import FilterIcon from 'assets/icons/mono/FilterIcon';
import Filters, { defaultFiltersValue } from 'Components/campaigns/collaborations/milestone/Filters';
import { ApplicationsFilters } from 'types/campaign/applications';
import { useIdleTimer } from "react-idle-timer";
import SimpleBar from 'simplebar-react';
import TaskFilters from 'Components/campaigns/collaborations/TaskFilters';
import { useMediaQuery } from 'hooks/useMediaQuery';
const CollaborationLayout: React.FC<{ children: React.ReactNode | React.ReactNode[] }> = ({ children }) => {
    useDocumentTitle("Collaborations - Campaign");
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const [filtersModalVisibility, showFiltersModal] = useState(false);
    const filtersDropdown = useRef<any>(null);
    const milestoneView: any = useRef();
    const [searchParams, setSearchParams] = useSearchParams();

    const queryFilters = searchParams.get('filters');
    const dispatch: AppDispatch = useDispatch();
    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters, defaultFiltersValue) : defaultFiltersValue;
        dispatch(updateMilestoneCollaborationFilters({ ...decodedFilters, page: 1 }))
    }, [queryFilters])


    const closeFiltersDropdown = () => {
        if (filtersDropdown.current) {
            filtersDropdown.current.toggle();
        }
    };

    const filters = useSelector((state: RootState) => state.collaborations.collaborations.defaultFilters);
    const [filterId, setFilterId] = useState(generateUniqueKey(filters, ['page']));

    const [searchQuery, setSearchQuery] = useState(filters.q ?? '');

    const { campaignId, influencerId, milestoneId } = useParams();

    const collaborationsData = useSelector((state: RootState) => state.collaborations.collaborations.filtered_lists[filterId!]);
    const collaborations = collaborationsData?.list ?? [];
    const page = collaborationsData?.page ?? 1;
    const has_more = collaborationsData?.has_more ?? true;

    const loading = useSelector((state: RootState) => state.collaborations.collaborations.loading);
    const error = useSelector((state: RootState) => state.collaborations.collaborations.error);

    const [autoFetchReq, setAutoFetchReq] = useState(false);


    const setFilters = (updatedFilters: MilestoneCollaborationsFilters) => {

        const encodedFilters = encodeBase64(updatedFilters);

        setSearchParams({ filters: encodedFilters });
        // dispatch(updateCollaborationsFilters(updatedFilters));
    }

    const [initialLoading, setInitialLoading] = useState(true);

    useEffect(() => {
        if (filters.page && filters.page === 1) {
            setInitialLoading(true);
        }
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            dispatch(fetchAllCollaborationsThunk({ filters })).finally(() => {
                setFilterId(generateUniqueKey(filters, ['page']));
                setInitialLoading(false);
            })
        }, 500)

    }, [filters])

    useEffect(() => {
        if (filters.q) {
            setSearchQuery(filters.q);
        }
    }, [filters.q])

    const nextPage = () => {
        if (has_more && !loading) {
            const nextPageNumber = page + 1;
            dispatch(fetchAllCollaborationsThunk({ filters: { ...filters, page: nextPageNumber } }))
        }
    }

    useEffect(() => {
        if (campaignId && influencerId) {
            if (window.innerWidth < 892) {
                milestoneView.current.classList.add("user-chat-show");
            }
        } else {
            if (window.innerWidth < 892) {
                milestoneView.current.classList.remove("user-chat-show");
            }
        }
    }, [campaignId, influencerId]);


    useIdleTimer({
        timeout: 1000 * 60 * 5, // 5 minutes
        onActive: () => {
            setAutoFetchReq(true);
            setTimeout(() => {
                fetchCollaborations(1, true, false);
            }, 100)
        },
        throttle: 1000,
    });

    const fetchCollaborations = async (page: number, force: boolean = false, showLoading = true) => {
        if (showLoading)
            setInitialLoading(true);

        dispatch(fetchAllCollaborationsThunk({ filters: { ...filters, page }, force })).finally(() => {
            setInitialLoading(false);
            setAutoFetchReq(false);
        });
    }


    const searchTask = (q: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (q.trim().length > 3) {
                setFilters({ ...filters, q: q.trim(), page: 1 });
            } else if (q.trim().length === 0) {
                setFilters({ ...filters, q: '', page: 1 });
            }
        }, 500);
        setSearchQuery(q);
    }

    return (
        <div className="collab-page pt-3 m-2">
            <Container fluid className='p-0'>
                <div className="chat-wrapper d-lg-flex gap-1 mt-n4 pt-1">
                    <div className="chat-leftsidebar overflow-hidden collab-left-side">
                        <div className="p-4">
                            <div className="d-flex align-items-center justify-content-between mb-3 gap-3">
                                <div className="d-flex align-items-center gap-2">
                                    <h5 className="m-0">Collaborations</h5> <span className="count-badge bg-dark-subtle fw-bold rounded-3 me-2">{collaborationsData?.count ?? 0}</span>
                                </div>
                                <div className="collab-search-container position-relative flex-grow-1">
                                    <span className="collab-search-icon">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M11.5 2.75C6.66751 2.75 2.75 6.66751 2.75 11.5C2.75 16.3325 6.66751 20.25 11.5 20.25C16.3325 20.25 20.25 16.3325 20.25 11.5C20.25 6.66751 16.3325 2.75 11.5 2.75ZM1.25 11.5C1.25 5.83908 5.83908 1.25 11.5 1.25C17.1609 1.25 21.75 5.83908 21.75 11.5C21.75 14.0605 20.8111 16.4017 19.2589 18.1982L22.5303 21.4697C22.8232 21.7626 22.8232 22.2374 22.5303 22.5303C22.2374 22.8232 21.7626 22.8232 21.4697 22.5303L18.1982 19.2589C16.4017 20.8111 14.0605 21.75 11.5 21.75C5.83908 21.75 1.25 17.1609 1.25 11.5Z" fill="black" fill-opacity="0.5" />
                                        </svg>
                                    </span>
                                    <input type="text" placeholder="Search..." value={searchQuery} onChange={(e) => searchTask(e.target.value)} className="collab-search-input" />
                                    {
                                        searchQuery && (
                                            <span className="search-clear-icon text-muted mt-1" onClick={() => {
                                                searchTask('');
                                            }}>
                                                <i className='bx bx-x fs-2' />
                                            </span>
                                        )
                                    }
                                </div>

                            </div>
                            <div className='d-flex align-items-center justify-content-between gap-2 mb-1'>
                                {/* */}
                                <button title='Refresh' onClick={() => fetchCollaborations(1, true)} className="new-btn new-btn-primary-outlined filters-btn-collab"><i className='fs-4 bx bx-refresh text-primary' ></i></button>
                                <UncontrolledDropdown className='flex-grow-1'>
                                    <DropdownToggle tag={"button"} className='new-btn new-btn-primary-outlined filters-btn-collab w-100 d-flex justify-content-between align-items-center p-1 p-md-2'>
                                        <span className='d-flex align-items-center gap-2'>
                                            <span className=''>Sort by: </span>
                                            {MilestoneCollaborationSortOptionLabel[filters.sort]} <i className={`mdi fs-5 ${filters.order === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'}`}></i>
                                        </span>
                                        <i className='mdi mdi-chevron-down fs-5' />
                                    </DropdownToggle>
                                    <DropdownMenu className='mt-1 p-0 w-100'>
                                        {
                                            Object.values(MilestoneCollaborationSortOptions).map((sortOption, i) => {
                                                return (
                                                    <React.Fragment key={i}>
                                                        {
                                                            ['asc', 'desc'].map((order, orderIndex) => {
                                                                return (
                                                                    <DropdownItem key={orderIndex} className='d-flex align-items-center justify-content-between' onClick={() => setFilters({ ...filters, sort: sortOption, order: order as 'asc' | 'desc' })}>
                                                                        <span>
                                                                            {MilestoneCollaborationSortOptionLabel[sortOption]}
                                                                        </span>
                                                                        <i className={`mdi fs-5 ${order === 'asc' ? 'mdi-sort-ascending' : 'mdi-sort-descending'}`}></i>
                                                                    </DropdownItem>
                                                                )
                                                            })
                                                        }
                                                    </React.Fragment>
                                                )
                                            })
                                        }
                                    </DropdownMenu>
                                </UncontrolledDropdown>
                                <button className='new-btn new-btn-primary-outlined filters-btn-collab' onClick={() => showFiltersModal(true)}><FilterIcon className="me-1" /> Filters</button>
                            </div>
                        </div>
                        <SimpleBar className="chat-room-list pt-3" style={{ margin: "-16px 0px 0px" }}>
                            <div className='px-4 pb-4'>
                                <span className='list-heading'>All Influencers</span>
                                <InfiniteScroll
                                    dataLength={collaborations.length}
                                    next={nextPage}
                                    hasMore={has_more}
                                    loader={<></>}
                                    className='list'
                                >
                                    {
                                        !initialLoading && collaborations.map((collaboration, i) => <CollaborationListItem isSelected={collaboration.influencer.uuid === influencerId && collaboration.campaign.uuid === campaignId} collaboration={collaboration} key={i} />)
                                    }
                                    {
                                        (loading || initialLoading) && !autoFetchReq && Array.from({ length: 4 }).map((_, i) => (
                                            <div key={i}>
                                                <Skeleton height={100} className='rounded-4' />
                                            </div>
                                        ))
                                    }
                                    {
                                        !(loading || initialLoading) && collaborations.length === 0 &&
                                        <div className="d-flex w-100 justify-content-center align-items-center">
                                            <div className="length-zero">
                                                <h3>Currently there is no collaboration in your list, Try adjusting your filters or search query.</h3>
                                            </div>
                                        </div>
                                    }
                                    {
                                        !(loading || initialLoading) && error &&
                                        <div className="d-flex justify-content-center align-items-center">
                                            <button className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>Load More <i className='ms-2 fs-5 bx bx-refresh'></i></button>
                                        </div>
                                    }
                                </InfiniteScroll>
                            </div>
                        </SimpleBar>
                    </div>
                    <div className="user-chat w-100 overflow-hidden collab-right-side" ref={milestoneView}>
                        {children}
                    </div>
                </div>
            </Container >
            <TaskFilters
                isOpen={filtersModalVisibility}
                onClose={() => showFiltersModal(false)}
                defaultFilters={filters}
                onApply={(filters) => setFilters(filters)}
            />
        </div >
    )
}

export default CollaborationLayout